import { useState } from 'react'
import PianoKeyboard from './PianoKeyboard'
import { useMidiStore, VIRTUAL_KEYBOARD_INPUT_ID } from '@/stores/midiStore'

const MIN_OCTAVE = 0
const MAX_OCTAVE = 7
const MIN_CHANNEL = 1
const MAX_CHANNEL = 16
const MIN_VELOCITY = 1
const MAX_VELOCITY = 127

function clampVelocity(value: number): number {
  if (Number.isNaN(value)) return MIN_VELOCITY
  return Math.max(MIN_VELOCITY, Math.min(MAX_VELOCITY, Math.floor(value)))
}

/**
 * Performance-grade wrapper around `PianoKeyboard` for the dedicated
 * /midi page. Adds octave shift, MIDI channel selection, and a
 * compact summary of which outputs the keyboard is currently routed
 * to so the player can confirm at a glance which synths will fire.
 *
 * Routing itself is owned by `MidiRouter`; this component just shows
 * the live state and exposes the keyboard with bigger, more playable
 * keys.
 */
export function PerformanceKeyboard() {
  const [startOctave, setStartOctave] = useState(3)
  const [channel, setChannel] = useState(1)
  // Min and max are kept as a pair so a humanized range like 80-110
  // is one configuration step. Equal values give a fixed velocity.
  const [velocityMin, setVelocityMin] = useState(100)
  const [velocityMax, setVelocityMax] = useState(100)

  const updateVelocityMin = (raw: number) => {
    const next = clampVelocity(raw)
    setVelocityMin(next)
    if (next > velocityMax) setVelocityMax(next)
  }
  const updateVelocityMax = (raw: number) => {
    const next = clampVelocity(raw)
    setVelocityMax(next)
    if (next < velocityMin) setVelocityMin(next)
  }

  const routes = useMidiStore(s => s.routes)
  const hwOutputs = useMidiStore(s => s.hwOutputs)
  const routedOutputNames = routes
    .filter(r => r.inputId === VIRTUAL_KEYBOARD_INPUT_ID)
    .map(r => hwOutputs.find(o => o.id === r.outputId)?.name ?? r.outputId)

  return (
    <section className="bg-surface-raised border border-brd-subtle rounded-xl p-4 flex flex-col gap-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-content-primary">
            Performance Keyboard
          </h3>
          <p className="text-xs text-content-tertiary mt-0.5">
            Plays through the &quot;Virtual Keyboard&quot; input above. Route it
            to one or more outputs to play those instruments at once.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ControlGroup label="Octave">
            <button
              type="button"
              onClick={() => setStartOctave(o => Math.max(MIN_OCTAVE, o - 1))}
              disabled={startOctave <= MIN_OCTAVE}
              className="btn-secondary btn-sm disabled:opacity-50 px-2"
              aria-label="Shift down one octave"
            >
              -
            </button>
            <span className="text-xs font-mono w-6 text-center text-content-primary">
              {startOctave}
            </span>
            <button
              type="button"
              onClick={() => setStartOctave(o => Math.min(MAX_OCTAVE, o + 1))}
              disabled={startOctave >= MAX_OCTAVE}
              className="btn-secondary btn-sm disabled:opacity-50 px-2"
              aria-label="Shift up one octave"
            >
              +
            </button>
          </ControlGroup>
          <ControlGroup label="Channel">
            <select
              value={channel}
              onChange={e => setChannel(Number(e.target.value))}
              className="text-xs px-2 py-1 rounded border border-brd-subtle bg-surface-sunken text-content-primary"
              aria-label="MIDI channel"
            >
              {Array.from(
                { length: MAX_CHANNEL - MIN_CHANNEL + 1 },
                (_, i) => MIN_CHANNEL + i,
              ).map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </ControlGroup>
          <ControlGroup label="Velocity">
            <input
              type="number"
              min={MIN_VELOCITY}
              max={MAX_VELOCITY}
              step={1}
              value={velocityMin}
              onChange={e => updateVelocityMin(Number(e.target.value))}
              className="text-xs px-2 py-1 rounded border border-brd-subtle bg-surface-sunken text-content-primary w-14"
              aria-label="Minimum note-on velocity"
            />
            <span className="text-content-tertiary text-xs">&ndash;</span>
            <input
              type="number"
              min={MIN_VELOCITY}
              max={MAX_VELOCITY}
              step={1}
              value={velocityMax}
              onChange={e => updateVelocityMax(Number(e.target.value))}
              className="text-xs px-2 py-1 rounded border border-brd-subtle bg-surface-sunken text-content-primary w-14"
              aria-label="Maximum note-on velocity"
            />
          </ControlGroup>
        </div>
      </header>

      <div className="overflow-x-auto pb-2">
        <PianoKeyboard
          startOctave={startOctave}
          octaves={4}
          channel={channel}
          velocityMin={velocityMin}
          velocityMax={velocityMax}
          size="lg"
          showLabels
        />
      </div>

      <div className="flex items-center gap-2 text-xs text-content-secondary flex-wrap">
        <span className="text-content-tertiary uppercase tracking-wider text-[10px] font-semibold">
          Now playing
        </span>
        {routedOutputNames.length === 0 ? (
          <span className="italic text-content-tertiary">
            No outputs routed yet &mdash; add a route in the router above.
          </span>
        ) : (
          routedOutputNames.map(name => (
            <span
              key={name}
              className="px-2 py-0.5 rounded-full bg-[color:var(--second-soft)] text-second font-medium"
            >
              {name}
            </span>
          ))
        )}
      </div>

      <p className="text-[11px] text-content-tertiary">
        Use A-K for the lower octave (W/E/T/Y/U for sharps), or click / tap the
        keys.
      </p>
    </section>
  )
}

function ControlGroup({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-content-tertiary font-semibold">
        {label}
      </span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  )
}
